import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll, useMotionTemplate } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLines } from "./Reveal";
import { Overline } from "./Mark";
import { Clocks } from "./Clocks";
import { scrollToId } from "@/lib/lenis";

const ease = [0.22, 1, 0.36, 1];

const TiltFrame = ({ scrollY }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const imgY = useTransform(scrollY, [0, 800], ["0%", "18%"]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div className="[perspective:1400px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.5, ease }}
        className="relative mx-auto aspect-[3/4] w-full max-w-[460px]"
        data-testid="hero-tilt-frame"
      >
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0 round 240px 240px 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0 round 240px 240px 0 0)" }}
          transition={{ duration: 1.6, delay: 0.6, ease: [0.76, 0, 0.24, 1] }}
          className="absolute inset-0 overflow-hidden"
        >
          <motion.img src="/img/tower.jpg" alt="Illuminated residential tower at night" style={{ y: imgY }} className="h-[118%] w-full -translate-y-[9%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070E17] via-transparent to-transparent" />
        </motion.div>
        <div className="pointer-events-none absolute -inset-3 rounded-t-[250px] border border-[rgba(212,175,55,0.35)]" style={{ transform: "translateZ(-40px)" }} />
        <motion.div
          style={{ transform: "translateZ(70px)" }}
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 1.5, ease }}
          className="absolute -left-4 bottom-10 rounded-2xl border border-[rgba(212,175,55,0.3)] bg-[#0B192C]/80 px-5 py-4 backdrop-blur-xl sm:-left-10"
          data-testid="hero-floating-card"
        >
          <p className="font-mono-d text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]">Active markets</p>
          <p className="font-serif-d mt-1 text-2xl text-white">US · UK · NL</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const textY = useTransform(scrollY, [0, 800], [0, -90]);
  const sx = useMotionValue(-400);
  const sy = useMotionValue(-400);
  const spotlight = useMotionTemplate`radial-gradient(600px circle at ${sx}px ${sy}px, rgba(212,175,55,0.13), transparent 65%)`;
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    sx.set(e.clientX - r.left);
    sy.set(e.clientY - r.top);
  };

  return (
    <section id="home" ref={ref} onMouseMove={onMove} className="relative min-h-[100svh] overflow-hidden bg-[#070E17] pt-28" data-testid="hero-section">
      <motion.div className="pointer-events-none absolute inset-0" style={{ background: spotlight }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.35] [background-image:linear-gradient(rgba(212,175,55,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(212,175,55,0.07)_1px,transparent_1px)] [background-size:96px_96px] [mask-image:radial-gradient(ellipse_at_30%_40%,black,transparent_75%)]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-14 px-5 pb-16 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.3 }} className="flex items-center gap-4">
            <span className="h-px w-12 bg-[#D4AF37]" />
            <Overline>Property Consultancy &amp; Real Estate Marketing</Overline>
          </motion.div>
          <h1 className="font-serif-d mt-8 text-[2.9rem] font-medium leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-[5.6rem]" data-testid="hero-title">
            <MaskLines delay={0.35} lines={[
              "Connecting property",
              "opportunities with",
              <span key="g"><em className="gold-text font-serif-d italic">the right</em> audience.</span>,
            ]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.1, ease }} className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="hero-description">
            Real-estate marketing, property enquiry management and lead-generation support for property projects in the United States, the United Kingdom and the Netherlands.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.3, ease }} className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => scrollToId("contact")} className="group flex items-center gap-3 rounded-full bg-[#D4AF37] py-3 pl-7 pr-3 text-sm font-medium text-[#070E17] transition-[background-color,transform] duration-300 hover:bg-[#E6C280] active:scale-95" data-testid="hero-enquire-btn">
              Make an enquiry
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[#070E17] text-[#D4AF37] transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
            </button>
            <button onClick={() => scrollToId("services")} className="group flex items-center gap-2 rounded-full border border-[rgba(212,175,55,0.35)] px-7 py-4 text-sm text-white transition-[border-color,background-color] duration-300 hover:border-[#D4AF37] hover:bg-[#0F1E36]" data-testid="hero-services-btn">
              Explore services <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.6 }} className="mt-14 max-w-lg border-t border-[rgba(212,175,55,0.18)] pt-6">
            <Clocks />
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5">
          <TiltFrame scrollY={scrollY} />
        </div>
      </div>
    </section>
  );
};
